{
  "meta": {
    "version": "2.0",
    "source_version": "anuka-eq-skill-tree-v2",
    "name_en": "EMO.coach Progressive Skill Tree",
    "name_ru": "Прогрессивное дерево навыков EMO.coach",
    "description_en": "A progressive disclosure EQ skill tree with 4 domains, 24 skills, 96 levels, and 4 immersion levels per domain.",
    "description_ru": "Прогрессивное дерево развития EQ: 4 домена, 24 навыка, 96 уровней и 4 уровня погружения в каждом домене.",
    "created": "2026-07-07",
    "total_domains": 4,
    "total_skills": 24,
    "total_levels": 96,
    "immersion_levels": 4,
    "focus": "B2C personal development + progressive proof-based disclosure",
    "reference_note": "Skill names and behaviors are original Anuka product content informed by public EQ frameworks; this seed is not an EQ-i, Six Seconds, or CASEL assessment."
  },
  "domains": [
    {
      "id": "self_awareness",
      "name_en": "Self-Awareness",
      "name_ru": "Самосознание",
      "description_en": "Recognize emotions, patterns, values, needs, and boundaries before they drive behavior.",
      "description_ru": "Умение распознавать эмоции, паттерны, ценности, потребности и границы до того, как они управляют поведением.",
      "icon": "🧠",
      "color": "#6366F1",
      "immersion_levels": [
        {
          "level": 1,
          "name_en": "Starter",
          "name_ru": "Старт",
          "visible_skill_ids": ["emotional_self_awareness", "accurate_self_assessment"],
          "unlock_requirement_en": "Practice each starter skill at least once."
        },
        {
          "level": 2,
          "name_en": "Builder",
          "name_ru": "Развитие",
          "visible_skill_ids": [
            "emotional_self_awareness",
            "accurate_self_assessment",
            "values_purpose_awareness",
            "emotional_patterns_triggers"
          ],
          "unlock_requirement_en": "Build activity across starter and builder skills."
        },
        {
          "level": 3,
          "name_en": "Expander",
          "name_ru": "Расширение",
          "visible_skill_ids": [
            "emotional_self_awareness",
            "accurate_self_assessment",
            "values_purpose_awareness",
            "emotional_patterns_triggers",
            "self_regard_compassion",
            "needs_boundaries_awareness"
          ],
          "unlock_requirement_en": "Show activity across most visible skills in the domain."
        },
        {
          "level": 4,
          "name_en": "Mastery",
          "name_ru": "Мастерство",
          "visible_skill_ids": [
            "emotional_self_awareness",
            "accurate_self_assessment",
            "values_purpose_awareness",
            "emotional_patterns_triggers",
            "self_regard_compassion",
            "needs_boundaries_awareness"
          ],
          "mastery_unlocked": true,
          "unlock_requirement_en": "Use the full domain as a capstone layer."
        }
      ],
      "skills": [
        {
          "id": "emotional_self_awareness",
          "name_en": "Emotional Self-Awareness",
          "name_ru": "Эмоциональное самосознание",
          "icon": "🧠",
          "unlock_stage": 1,
          "sub_skills": ["emotion_labeling", "body_signals", "emotion_to_action"],
          "description_en": "Name emotions accurately in the moment and understand their source, body signal, and behavioral impact.",
          "description_ru": "Точно называть эмоции в моменте и понимать их источник, телесный сигнал и влияние на поведение.",
          "why_important_en": "Emotion naming turns reaction into information before it becomes a decision.",
          "why_important_ru": "Точное называние эмоций превращает реакцию в информацию до того, как она станет решением.",
          "examples_en": [
            "Use Emotional Self-Awareness in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Emotional Self-Awareness practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Эмоциональное самосознание in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Эмоциональное самосознание practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Emotional Self-Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Emotional Self-Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Emotional Self-Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Emotional Self-Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Emotional Self-Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Emotional Self-Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Emotional Self-Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Emotional Self-Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "accurate_self_assessment",
          "name_en": "Accurate Self-Assessment",
          "name_ru": "Точная самооценка",
          "icon": "🔎",
          "unlock_stage": 1,
          "sub_skills": ["strengths_limits", "feedback_seeking", "confidence_calibration"],
          "description_en": "See strengths, limits, blind spots, and confidence level without exaggeration or self-attack.",
          "description_ru": "Реалистично видеть сильные стороны, ограничения, слепые зоны и уровень уверенности без преувеличения и самокритики.",
          "why_important_en": "A realistic self-view protects pricing, scope, hiring, and feedback decisions.",
          "why_important_ru": "Реалистичная самооценка защищает решения о цене, объеме работы, найме и обратной связи.",
          "examples_en": [
            "Use Accurate Self-Assessment in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Accurate Self-Assessment practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Точная самооценка in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Точная самооценка practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Accurate Self-Assessment behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Accurate Self-Assessment today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Accurate Self-Assessment behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Accurate Self-Assessment today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Accurate Self-Assessment behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Accurate Self-Assessment today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Accurate Self-Assessment behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Accurate Self-Assessment today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "values_purpose_awareness",
          "name_en": "Values, Purpose & Strengths Awareness",
          "name_ru": "Осознание ценностей, цели и сильных сторон",
          "icon": "🧭",
          "unlock_stage": 2,
          "sub_skills": ["values_clarity", "purpose_language", "strengths_use"],
          "description_en": "Connect daily choices to values, purpose, strengths, and the kind of work you want to build.",
          "description_ru": "Связывать ежедневные решения с ценностями, целью, сильными сторонами и тем, какую работу хочется строить.",
          "why_important_en": "Values create steadiness when external judgment gets noisy.",
          "why_important_ru": "Ценности дают устойчивость, когда внешний шум становится сильным.",
          "examples_en": [
            "Use Values, Purpose & Strengths Awareness in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Values, Purpose & Strengths Awareness practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Осознание ценностей, цели и сильных сторон in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Осознание ценностей, цели и сильных сторон practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Values, Purpose & Strengths Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Values, Purpose & Strengths Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Values, Purpose & Strengths Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Values, Purpose & Strengths Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Values, Purpose & Strengths Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Values, Purpose & Strengths Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Values, Purpose & Strengths Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Values, Purpose & Strengths Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "emotional_patterns_triggers",
          "name_en": "Emotional Patterns & Triggers",
          "name_ru": "Эмоциональные паттерны и триггеры",
          "icon": "⚡",
          "unlock_stage": 2,
          "sub_skills": ["trigger_mapping", "pattern_review", "early_warning_signs"],
          "description_en": "Recognize repeated reactions, trigger loops, and the situations that reliably pull you off center.",
          "description_ru": "Распознавать повторяющиеся реакции, триггерные петли и ситуации, которые выбивают из равновесия.",
          "why_important_en": "Patterns become workable only after they are named and tracked.",
          "why_important_ru": "Паттерны становятся управляемыми только после того, как их назвали и отследили.",
          "examples_en": [
            "Use Emotional Patterns & Triggers in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Emotional Patterns & Triggers practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Эмоциональные паттерны и триггеры in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Эмоциональные паттерны и триггеры practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Emotional Patterns & Triggers behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Emotional Patterns & Triggers today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Emotional Patterns & Triggers behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Emotional Patterns & Triggers today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Emotional Patterns & Triggers behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Emotional Patterns & Triggers today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Emotional Patterns & Triggers behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Emotional Patterns & Triggers today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "self_regard_compassion",
          "name_en": "Self-Regard & Self-Compassion",
          "name_ru": "Самоуважение и самосострадание",
          "icon": "🤲",
          "unlock_stage": 3,
          "sub_skills": ["self_regard", "repair_without_shame", "fair_inner_voice"],
          "description_en": "Hold a fair view of yourself while learning from mistakes and pressure.",
          "description_ru": "Сохранять справедливое отношение к себе, учась на ошибках и давлении.",
          "why_important_en": "People recover faster when accountability is not mixed with self-contempt.",
          "why_important_ru": "Восстановление быстрее, когда ответственность не смешана с презрением к себе.",
          "examples_en": [
            "Use Self-Regard & Self-Compassion in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Self-Regard & Self-Compassion practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Самоуважение и самосострадание in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Самоуважение и самосострадание practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Self-Regard & Self-Compassion behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Self-Regard & Self-Compassion today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Self-Regard & Self-Compassion behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Self-Regard & Self-Compassion today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Self-Regard & Self-Compassion behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Self-Regard & Self-Compassion today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Self-Regard & Self-Compassion behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Self-Regard & Self-Compassion today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "needs_boundaries_awareness",
          "name_en": "Needs & Boundaries Awareness",
          "name_ru": "Осознание потребностей и границ",
          "icon": "🚧",
          "unlock_stage": 3,
          "sub_skills": ["needs_language", "boundary_signals", "energy_costs"],
          "description_en": "Notice needs, limits, energy costs, and boundary signals before resentment builds.",
          "description_ru": "Замечать потребности, ограничения, цену энергии и сигналы границ до накопления раздражения.",
          "why_important_en": "Clear needs and boundaries prevent avoidable conflict and burnout.",
          "why_important_ru": "Ясные потребности и границы предотвращают лишние конфликты и выгорание.",
          "examples_en": [
            "Use Needs & Boundaries Awareness in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Needs & Boundaries Awareness practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Осознание потребностей и границ in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Осознание потребностей и границ practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Needs & Boundaries Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Needs & Boundaries Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Needs & Boundaries Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Needs & Boundaries Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Needs & Boundaries Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Needs & Boundaries Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Needs & Boundaries Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Needs & Boundaries Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        }
      ]
    },
    {
      "id": "self_management",
      "name_en": "Self-Management",
      "name_ru": "Самоуправление",
      "description_en": "Regulate emotions, recover from pressure, choose intentionally, and follow through.",
      "description_ru": "Умение регулировать эмоции, восстанавливаться под давлением, выбирать осознанно и доводить дела до конца.",
      "icon": "🛡️",
      "color": "#10B981",
      "immersion_levels": [
        {
          "level": 1,
          "name_en": "Starter",
          "name_ru": "Старт",
          "visible_skill_ids": ["emotional_self_control", "stress_tolerance_recovery"],
          "unlock_requirement_en": "Practice each starter skill at least once."
        },
        {
          "level": 2,
          "name_en": "Builder",
          "name_ru": "Развитие",
          "visible_skill_ids": [
            "emotional_self_control",
            "stress_tolerance_recovery",
            "adaptability_resilience",
            "consequential_thinking"
          ],
          "unlock_requirement_en": "Build activity across starter and builder skills."
        },
        {
          "level": 3,
          "name_en": "Expander",
          "name_ru": "Расширение",
          "visible_skill_ids": [
            "emotional_self_control",
            "stress_tolerance_recovery",
            "adaptability_resilience",
            "consequential_thinking",
            "achievement_orientation",
            "intrinsic_motivation_follow_through"
          ],
          "unlock_requirement_en": "Show activity across most visible skills in the domain."
        },
        {
          "level": 4,
          "name_en": "Mastery",
          "name_ru": "Мастерство",
          "visible_skill_ids": [
            "emotional_self_control",
            "stress_tolerance_recovery",
            "adaptability_resilience",
            "consequential_thinking",
            "achievement_orientation",
            "intrinsic_motivation_follow_through"
          ],
          "mastery_unlocked": true,
          "unlock_requirement_en": "Use the full domain as a capstone layer."
        }
      ],
      "skills": [
        {
          "id": "emotional_self_control",
          "name_en": "Emotional Self-Control",
          "name_ru": "Эмоциональный самоконтроль",
          "icon": "⏸️",
          "unlock_stage": 1,
          "sub_skills": ["pause", "impulse_control", "calm_response"],
          "description_en": "Create space between stimulus and response when emotion is strong.",
          "description_ru": "Создавать паузу между стимулом и ответом, когда эмоция сильна.",
          "why_important_en": "A pause turns impulse into choice.",
          "why_important_ru": "Пауза превращает импульс в выбор.",
          "examples_en": [
            "Use Emotional Self-Control in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Emotional Self-Control practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Эмоциональный самоконтроль in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Эмоциональный самоконтроль practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Emotional Self-Control behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Emotional Self-Control today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Emotional Self-Control behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Emotional Self-Control today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Emotional Self-Control behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Emotional Self-Control today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Emotional Self-Control behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Emotional Self-Control today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "stress_tolerance_recovery",
          "name_en": "Stress Tolerance & Recovery",
          "name_ru": "Стрессоустойчивость и восстановление",
          "icon": "🌿",
          "unlock_stage": 1,
          "sub_skills": ["body_reset", "recovery_window", "pressure_capacity"],
          "description_en": "Bring the body and attention back to a workable state under pressure.",
          "description_ru": "Возвращать тело и внимание в рабочее состояние под давлением.",
          "why_important_en": "Recovery lowers the hidden cost of hard decisions.",
          "why_important_ru": "Восстановление снижает скрытую цену сложных решений.",
          "examples_en": [
            "Use Stress Tolerance & Recovery in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Stress Tolerance & Recovery practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Стрессоустойчивость и восстановление in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Стрессоустойчивость и восстановление practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Stress Tolerance & Recovery behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Stress Tolerance & Recovery today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Stress Tolerance & Recovery behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Stress Tolerance & Recovery today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Stress Tolerance & Recovery behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Stress Tolerance & Recovery today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Stress Tolerance & Recovery behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Stress Tolerance & Recovery today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "adaptability_resilience",
          "name_en": "Adaptability & Resilience",
          "name_ru": "Адаптивность и устойчивость",
          "icon": "🔁",
          "unlock_stage": 2,
          "sub_skills": ["plan_reframing", "setback_recovery", "flexible_strategy"],
          "description_en": "Adjust plans, recover from setbacks, and stay useful when conditions change.",
          "description_ru": "Менять планы, восстанавливаться после неудач и оставаться полезным при изменениях.",
          "why_important_en": "Adaptability keeps uncertainty from becoming paralysis.",
          "why_important_ru": "Адаптивность не дает неопределенности превратиться в паралич.",
          "examples_en": [
            "Use Adaptability & Resilience in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Adaptability & Resilience practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Адаптивность и устойчивость in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Адаптивность и устойчивость practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Adaptability & Resilience behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Adaptability & Resilience today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Adaptability & Resilience behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Adaptability & Resilience today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Adaptability & Resilience behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Adaptability & Resilience today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Adaptability & Resilience behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Adaptability & Resilience today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "consequential_thinking",
          "name_en": "Consequential Thinking",
          "name_ru": "Мышление последствиями",
          "icon": "🧮",
          "unlock_stage": 2,
          "sub_skills": ["choice_costs", "tradeoff_review", "future_impact"],
          "description_en": "Consider emotional, relational, and practical consequences before acting.",
          "description_ru": "Учитывать эмоциональные, отношенческие и практические последствия до действия.",
          "why_important_en": "Better choices come from seeing second-order effects before they arrive.",
          "why_important_ru": "Лучшие решения появляются, когда видишь последствия заранее.",
          "examples_en": [
            "Use Consequential Thinking in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Consequential Thinking practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Мышление последствиями in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Мышление последствиями practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Consequential Thinking behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Consequential Thinking today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Consequential Thinking behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Consequential Thinking today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Consequential Thinking behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Consequential Thinking today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Consequential Thinking behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Consequential Thinking today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "achievement_orientation",
          "name_en": "Achievement Orientation & Positive Outlook",
          "name_ru": "Ориентация на результат и позитивный взгляд",
          "icon": "🎯",
          "unlock_stage": 3,
          "sub_skills": ["quality_standards", "goal_tracking", "optimistic_persistence"],
          "description_en": "Set meaningful goals, raise standards, and keep constructive momentum through long cycles.",
          "description_ru": "Ставить значимые цели, повышать стандарты и сохранять конструктивный темп в длинных циклах.",
          "why_important_en": "Ambitious work needs standards that do not collapse under delay.",
          "why_important_ru": "Амбициозная работа требует стандартов, которые не рушатся при задержках.",
          "examples_en": [
            "Use Achievement Orientation & Positive Outlook in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Achievement Orientation & Positive Outlook practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Ориентация на результат и позитивный взгляд in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Ориентация на результат и позитивный взгляд practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Achievement Orientation & Positive Outlook behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Achievement Orientation & Positive Outlook today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Achievement Orientation & Positive Outlook behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Achievement Orientation & Positive Outlook today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Achievement Orientation & Positive Outlook behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Achievement Orientation & Positive Outlook today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Achievement Orientation & Positive Outlook behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Achievement Orientation & Positive Outlook today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "intrinsic_motivation_follow_through",
          "name_en": "Intrinsic Motivation & Follow-Through",
          "name_ru": "Внутренняя мотивация и доведение до конца",
          "icon": "✅",
          "unlock_stage": 3,
          "sub_skills": ["commitment_design", "self_accountability", "visible_completion"],
          "description_en": "Use values and commitments to complete visible next steps without constant external pressure.",
          "description_ru": "Опираясь на ценности и обязательства, доводить видимые шаги до конца без постоянного внешнего давления.",
          "why_important_en": "Self-trust is built by closing loops.",
          "why_important_ru": "Доверие к себе строится через закрытые циклы.",
          "examples_en": [
            "Use Intrinsic Motivation & Follow-Through in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Intrinsic Motivation & Follow-Through practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Внутренняя мотивация и доведение до конца in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Внутренняя мотивация и доведение до конца practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Intrinsic Motivation & Follow-Through behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Intrinsic Motivation & Follow-Through today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Intrinsic Motivation & Follow-Through behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Intrinsic Motivation & Follow-Through today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Intrinsic Motivation & Follow-Through behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Intrinsic Motivation & Follow-Through today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Intrinsic Motivation & Follow-Through behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Intrinsic Motivation & Follow-Through today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        }
      ]
    },
    {
      "id": "social_awareness",
      "name_en": "Social Awareness",
      "name_ru": "Социальная осознанность",
      "description_en": "Read people, context, needs, nonverbal signals, and cultural dynamics with care.",
      "description_ru": "Умение считывать людей, контекст, потребности, невербальные сигналы и культурную динамику.",
      "icon": "👁️",
      "color": "#8B5CF6",
      "immersion_levels": [
        {
          "level": 1,
          "name_en": "Starter",
          "name_ru": "Старт",
          "visible_skill_ids": ["empathy_perspective_taking", "active_listening_presence"],
          "unlock_requirement_en": "Practice each starter skill at least once."
        },
        {
          "level": 2,
          "name_en": "Builder",
          "name_ru": "Развитие",
          "visible_skill_ids": [
            "empathy_perspective_taking",
            "active_listening_presence",
            "contextual_organizational_awareness",
            "nonverbal_emotional_cues"
          ],
          "unlock_requirement_en": "Build activity across starter and builder skills."
        },
        {
          "level": 3,
          "name_en": "Expander",
          "name_ru": "Расширение",
          "visible_skill_ids": [
            "empathy_perspective_taking",
            "active_listening_presence",
            "contextual_organizational_awareness",
            "nonverbal_emotional_cues",
            "service_orientation",
            "inclusion_cultural_humility"
          ],
          "unlock_requirement_en": "Show activity across most visible skills in the domain."
        },
        {
          "level": 4,
          "name_en": "Mastery",
          "name_ru": "Мастерство",
          "visible_skill_ids": [
            "empathy_perspective_taking",
            "active_listening_presence",
            "contextual_organizational_awareness",
            "nonverbal_emotional_cues",
            "service_orientation",
            "inclusion_cultural_humility"
          ],
          "mastery_unlocked": true,
          "unlock_requirement_en": "Use the full domain as a capstone layer."
        }
      ],
      "skills": [
        {
          "id": "empathy_perspective_taking",
          "name_en": "Empathy & Perspective-Taking",
          "name_ru": "Эмпатия и принятие перспективы",
          "icon": "🤲",
          "unlock_stage": 1,
          "sub_skills": ["perspective_taking", "emotion_reflection", "care_signal"],
          "description_en": "Understand another person’s feelings, needs, and point of view without losing your own.",
          "description_ru": "Понимать чувства, потребности и точку зрения другого человека, не теряя свою.",
          "why_important_en": "People cooperate more when they feel accurately understood.",
          "why_important_ru": "Люди сотрудничают легче, когда чувствуют, что их точно поняли.",
          "examples_en": [
            "Use Empathy & Perspective-Taking in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Empathy & Perspective-Taking practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Эмпатия и принятие перспективы in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Эмпатия и принятие перспективы practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Empathy & Perspective-Taking behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Empathy & Perspective-Taking today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Empathy & Perspective-Taking behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Empathy & Perspective-Taking today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Empathy & Perspective-Taking behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Empathy & Perspective-Taking today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Empathy & Perspective-Taking behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Empathy & Perspective-Taking today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "active_listening_presence",
          "name_en": "Active Listening & Presence",
          "name_ru": "Активное слушание и присутствие",
          "icon": "👂",
          "unlock_stage": 1,
          "sub_skills": ["reflecting_back", "second_question", "presence"],
          "description_en": "Listen for meaning, emotion, and the unsaid constraint before advising or defending.",
          "description_ru": "Слушать смысл, эмоцию и скрытое ограничение до совета или защиты.",
          "why_important_en": "Listening quality often decides whether feedback becomes useful.",
          "why_important_ru": "Качество слушания часто решает, станет ли обратная связь полезной.",
          "examples_en": [
            "Use Active Listening & Presence in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Active Listening & Presence practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Активное слушание и присутствие in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Активное слушание и присутствие practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Active Listening & Presence behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Active Listening & Presence today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Active Listening & Presence behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Active Listening & Presence today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Active Listening & Presence behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Active Listening & Presence today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Active Listening & Presence behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Active Listening & Presence today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "contextual_organizational_awareness",
          "name_en": "Contextual & Organizational Awareness",
          "name_ru": "Контекстная и организационная осознанность",
          "icon": "🗺️",
          "unlock_stage": 2,
          "sub_skills": ["read_the_room", "power_dynamics", "norms_awareness"],
          "description_en": "Read the room, power dynamics, incentives, norms, and organizational context.",
          "description_ru": "Считывать ситуацию, динамику власти, стимулы, нормы и организационный контекст.",
          "why_important_en": "Context often explains behavior better than intent does.",
          "why_important_ru": "Контекст часто объясняет поведение лучше, чем намерение.",
          "examples_en": [
            "Use Contextual & Organizational Awareness in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Contextual & Organizational Awareness practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Контекстная и организационная осознанность in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Контекстная и организационная осознанность practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Contextual & Organizational Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Contextual & Organizational Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Contextual & Organizational Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Contextual & Organizational Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Contextual & Organizational Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Contextual & Organizational Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Contextual & Organizational Awareness behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Contextual & Organizational Awareness today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "nonverbal_emotional_cues",
          "name_en": "Nonverbal Emotional Cues",
          "name_ru": "Невербальные эмоциональные сигналы",
          "icon": "👁️",
          "unlock_stage": 2,
          "sub_skills": ["tone_pace", "facial_cues", "silence_reading"],
          "description_en": "Notice tone, pace, facial cues, silence, and body language without overclaiming certainty.",
          "description_ru": "Замечать тон, темп, мимику, паузы и язык тела без чрезмерной уверенности.",
          "why_important_en": "Nonverbal signals add early data before a conversation becomes explicit.",
          "why_important_ru": "Невербальные сигналы дают ранние данные до того, как разговор станет явным.",
          "examples_en": [
            "Use Nonverbal Emotional Cues in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Nonverbal Emotional Cues practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Невербальные эмоциональные сигналы in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Невербальные эмоциональные сигналы practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Nonverbal Emotional Cues behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Nonverbal Emotional Cues today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Nonverbal Emotional Cues behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Nonverbal Emotional Cues today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Nonverbal Emotional Cues behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Nonverbal Emotional Cues today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Nonverbal Emotional Cues behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Nonverbal Emotional Cues today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "service_orientation",
          "name_en": "Service Orientation & Attunement",
          "name_ru": "Ориентация на пользу и настройка на потребности",
          "icon": "🧩",
          "unlock_stage": 3,
          "sub_skills": ["need_discovery", "value_fit", "client_care"],
          "description_en": "Discover real needs and offer value that fits the person, client, or team.",
          "description_ru": "Выявлять реальные потребности и предлагать ценность, подходящую человеку, клиенту или команде.",
          "why_important_en": "Useful help starts with the other person’s actual need.",
          "why_important_ru": "Полезная помощь начинается с реальной потребности другого человека.",
          "examples_en": [
            "Use Service Orientation & Attunement in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Service Orientation & Attunement practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Ориентация на пользу и настройка на потребности in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Ориентация на пользу и настройка на потребности practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Service Orientation & Attunement behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Service Orientation & Attunement today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Service Orientation & Attunement behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Service Orientation & Attunement today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Service Orientation & Attunement behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Service Orientation & Attunement today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Service Orientation & Attunement behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Service Orientation & Attunement today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "inclusion_cultural_humility",
          "name_en": "Inclusion & Cultural Humility",
          "name_ru": "Инклюзивность и культурная скромность",
          "icon": "🌍",
          "unlock_stage": 3,
          "sub_skills": ["identity_awareness", "humble_questions", "inclusive_adjustment"],
          "description_en": "Stay curious across identity, culture, role, and background differences.",
          "description_ru": "Сохранять любопытство при различиях в идентичности, культуре, роли и опыте.",
          "why_important_en": "Humility keeps difference from becoming distance.",
          "why_important_ru": "Скромность не дает различиям превращаться в дистанцию.",
          "examples_en": [
            "Use Inclusion & Cultural Humility in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Inclusion & Cultural Humility practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Инклюзивность и культурная скромность in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Инклюзивность и культурная скромность practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Inclusion & Cultural Humility behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Inclusion & Cultural Humility today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Inclusion & Cultural Humility behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Inclusion & Cultural Humility today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Inclusion & Cultural Humility behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Inclusion & Cultural Humility today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Inclusion & Cultural Humility behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Inclusion & Cultural Humility today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        }
      ]
    },
    {
      "id": "relationship_management",
      "name_en": "Relationship Management",
      "name_ru": "Управление отношениями",
      "description_en": "Communicate, influence, repair, collaborate, and build trust across difficult moments.",
      "description_ru": "Умение общаться, влиять, восстанавливать доверие, сотрудничать и строить отношения в сложные моменты.",
      "icon": "🤝",
      "color": "#F97316",
      "immersion_levels": [
        {
          "level": 1,
          "name_en": "Starter",
          "name_ru": "Старт",
          "visible_skill_ids": ["influence_persuasion", "conflict_management"],
          "unlock_requirement_en": "Practice each starter skill at least once."
        },
        {
          "level": 2,
          "name_en": "Builder",
          "name_ru": "Развитие",
          "visible_skill_ids": [
            "influence_persuasion",
            "conflict_management",
            "cross_cultural_intelligence",
            "trust_repair"
          ],
          "unlock_requirement_en": "Build activity across starter and builder skills."
        },
        {
          "level": 3,
          "name_en": "Expander",
          "name_ru": "Расширение",
          "visible_skill_ids": [
            "influence_persuasion",
            "conflict_management",
            "cross_cultural_intelligence",
            "trust_repair",
            "clear_communication_requests",
            "collaborative_leadership"
          ],
          "unlock_requirement_en": "Show activity across most visible skills in the domain."
        },
        {
          "level": 4,
          "name_en": "Mastery",
          "name_ru": "Мастерство",
          "visible_skill_ids": [
            "influence_persuasion",
            "conflict_management",
            "cross_cultural_intelligence",
            "trust_repair",
            "clear_communication_requests",
            "collaborative_leadership"
          ],
          "mastery_unlocked": true,
          "unlock_requirement_en": "Use the full domain as a capstone layer."
        }
      ],
      "skills": [
        {
          "id": "influence_persuasion",
          "name_en": "Influence & Persuasion",
          "name_ru": "Влияние и убеждение",
          "icon": "✨",
          "unlock_stage": 1,
          "sub_skills": ["shared_interest", "storytelling", "ethical_persuasion"],
          "description_en": "Move people toward action through trust, relevance, story, and respect for autonomy.",
          "description_ru": "Помогать людям двигаться к действию через доверие, релевантность, историю и уважение к автономии.",
          "why_important_en": "Mature influence builds partnership instead of resistance.",
          "why_important_ru": "Зрелое влияние строит партнерство вместо сопротивления.",
          "examples_en": [
            "Use Influence & Persuasion in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Influence & Persuasion practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Влияние и убеждение in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Влияние и убеждение practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Influence & Persuasion behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Influence & Persuasion today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Influence & Persuasion behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Influence & Persuasion today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Influence & Persuasion behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Influence & Persuasion today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Influence & Persuasion behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Influence & Persuasion today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "conflict_management",
          "name_en": "Conflict Management",
          "name_ru": "Управление конфликтом",
          "icon": "🧩",
          "unlock_stage": 1,
          "sub_skills": ["hard_conversation", "common_ground", "option_building"],
          "description_en": "Stay engaged in difficult conversations, name tensions, and move toward workable options.",
          "description_ru": "Оставаться в сложных разговорах, называть напряжение и двигаться к рабочим вариантам.",
          "why_important_en": "Strong relationships are measured by repair, not by the absence of conflict.",
          "why_important_ru": "Сильные отношения измеряются восстановлением, а не отсутствием конфликта.",
          "examples_en": [
            "Use Conflict Management in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Conflict Management practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Управление конфликтом in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Управление конфликтом practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Conflict Management behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Conflict Management today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Conflict Management behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Conflict Management today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Conflict Management behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Conflict Management today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Conflict Management behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Conflict Management today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "cross_cultural_intelligence",
          "name_en": "Cross-Cultural Intelligence",
          "name_ru": "Кросс-культурный интеллект",
          "icon": "🌐",
          "unlock_stage": 2,
          "sub_skills": ["cultural_norms", "bias_notice", "etiquette_adjustment"],
          "description_en": "Adapt communication, etiquette, pace, and trust-building across cultures and norms.",
          "description_ru": "Адаптировать коммуникацию, этикет, темп и построение доверия между культурами и нормами.",
          "why_important_en": "Cultural intelligence protects trust when defaults differ.",
          "why_important_ru": "Культурный интеллект защищает доверие, когда привычные нормы отличаются.",
          "examples_en": [
            "Use Cross-Cultural Intelligence in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Cross-Cultural Intelligence practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Кросс-культурный интеллект in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Кросс-культурный интеллект practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Cross-Cultural Intelligence behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Cross-Cultural Intelligence today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Cross-Cultural Intelligence behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Cross-Cultural Intelligence today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Cross-Cultural Intelligence behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Cross-Cultural Intelligence today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Cross-Cultural Intelligence behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Cross-Cultural Intelligence today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "trust_repair",
          "name_en": "Trust Repair",
          "name_ru": "Восстановление доверия",
          "icon": "🪢",
          "unlock_stage": 2,
          "sub_skills": ["accountability", "repair_followup", "reliability_rebuild"],
          "description_en": "Own impact, follow up after tension, and rebuild reliability through observable behavior.",
          "description_ru": "Признавать влияние, возвращаться после напряжения и восстанавливать надежность через видимое поведение.",
          "why_important_en": "Trust returns through repeated evidence, not explanation alone.",
          "why_important_ru": "Доверие возвращается через повторяемые доказательства, а не только объяснения.",
          "examples_en": [
            "Use Trust Repair in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Trust Repair practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Восстановление доверия in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Восстановление доверия practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Trust Repair behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Trust Repair today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Trust Repair behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Trust Repair today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Trust Repair behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Trust Repair today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Trust Repair behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Trust Repair today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "clear_communication_requests",
          "name_en": "Clear Communication & Requests",
          "name_ru": "Ясная коммуникация и просьбы",
          "icon": "📣",
          "unlock_stage": 3,
          "sub_skills": ["request_design", "agreement_check", "next_step_clarity"],
          "description_en": "Make requests, agreements, ownership, and next steps clear enough to act on.",
          "description_ru": "Делать просьбы, договоренности, ответственность и следующие шаги достаточно ясными для действия.",
          "why_important_en": "Clear requests reduce friction and protect trust.",
          "why_important_ru": "Ясные просьбы снижают трение и защищают доверие.",
          "examples_en": [
            "Use Clear Communication & Requests in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Clear Communication & Requests practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Ясная коммуникация и просьбы in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Ясная коммуникация и просьбы practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Clear Communication & Requests behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Clear Communication & Requests today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Clear Communication & Requests behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Clear Communication & Requests today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Clear Communication & Requests behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Clear Communication & Requests today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Clear Communication & Requests behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Clear Communication & Requests today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        },
        {
          "id": "collaborative_leadership",
          "name_en": "Collaborative Leadership",
          "name_ru": "Совместное лидерство",
          "icon": "🤝",
          "unlock_stage": 3,
          "sub_skills": ["shared_ownership", "facilitation", "team_alignment"],
          "description_en": "Create shared ownership, useful participation, and coordinated action without overcontrol.",
          "description_ru": "Создавать общую ответственность, полезное участие и согласованное действие без чрезмерного контроля.",
          "why_important_en": "Collaboration scales emotional intelligence beyond one conversation.",
          "why_important_ru": "Сотрудничество масштабирует эмоциональный интеллект за пределы одного разговора.",
          "examples_en": [
            "Use Collaborative Leadership in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Collaborative Leadership practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "examples_ru": [
            "Use Совместное лидерство in one real conversation and write what changed.",
            "Turn a current task, goal, or contact moment into a small Совместное лидерство practice rep.",
            "Capture a short proof note that shows the behavior instead of only describing intent."
          ],
          "levels": [
            {
              "level": 1,
              "title_en": "Notice: Recognize the behavior in a real moment",
              "title_ru": "Notice: Recognize the behavior in a real moment",
              "action_en": "Use one short practice rep and capture what you noticed.",
              "action_ru": "Use one short practice rep and capture what you noticed.",
              "proof_suggestion_en": "Short note or 30-60 sec reflection showing the moment.",
              "proof_suggestion_ru": "Short note or 30-60 sec reflection showing the moment.",
              "behavior_indicators": [
                "Names one observable Collaborative Leadership behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Collaborative Leadership today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 2,
              "title_en": "Apply: Use the behavior intentionally once",
              "title_ru": "Apply: Use the behavior intentionally once",
              "action_en": "Practice during a live task, conversation, or decision.",
              "action_ru": "Practice during a live task, conversation, or decision.",
              "proof_suggestion_en": "Reflection plus artifact, message, or evidence from the situation.",
              "proof_suggestion_ru": "Reflection plus artifact, message, or evidence from the situation.",
              "behavior_indicators": [
                "Names one observable Collaborative Leadership behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Collaborative Leadership today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 3,
              "title_en": "Adapt: Adjust the behavior under pressure",
              "title_ru": "Adapt: Adjust the behavior under pressure",
              "action_en": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "action_ru": "Use the skill when stakes, emotion, or ambiguity are higher.",
              "proof_suggestion_en": "Before/after review with what changed and why.",
              "proof_suggestion_ru": "Before/after review with what changed and why.",
              "behavior_indicators": [
                "Names one observable Collaborative Leadership behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Collaborative Leadership today?",
                "What would make this behavior visible as proof?"
              ]
            },
            {
              "level": 4,
              "title_en": "Sustain: Show repeatable behavior across contexts",
              "title_ru": "Sustain: Show repeatable behavior across contexts",
              "action_en": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "action_ru": "Repeat the behavior across multiple moments and connect it to outcomes.",
              "proof_suggestion_en": "Proof set with reflection, result, and optional outside feedback.",
              "proof_suggestion_ru": "Proof set with reflection, result, and optional outside feedback.",
              "behavior_indicators": [
                "Names one observable Collaborative Leadership behavior",
                "Connects the behavior to a real task, proof, contact, or goal"
              ],
              "coaching_prompts": [
                "Where can you practice Collaborative Leadership today?",
                "What would make this behavior visible as proof?"
              ]
            }
          ]
        }
      ]
    }
  ]
}
